import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LangQuoteListView } from '../../../localization/scripts/quotes/views/quote-list-view-lang';
import { DataCacheGeneric } from '../../../webmodule-common/cache/generic-data-cache';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { moneyToHtml } from '../../../webmodule-common/other/currency-formatter';
import { IStaleData } from '../../../webmodule-common/other/stale-data';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { EventGetReference, EventSnippet, EventTemplate, NullPromise } from '../../../webmodule-common/other/ui/events';
import { IconRefresh } from '../../../webmodule-common/other/ui/icons/icon-refresh';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { containsFlag, flagEquals } from '../../../webmodule-common/other/ui/string-helper-functions';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import { getApiFactory } from '../../api/api-injector';
import { BlobApi } from '../../api/blob-api';
import { ClientApi } from '../../api/client-api';
import { QuoteState, ResultGetQuoteSummary, ViewQuoteSummary } from '../../api/dealer-api-interface-quote';
import { QuoteApi } from '../../api/quote-api';
import { resourceClient } from '../../dealer-franchisee/clients/ui/launcher';
import { QuoteContainer, QuoteContainerManager } from '../data/quote-container';
import { UiQuoteUserInteraction } from './quote-user-interaction-ui';
import { launchQuote, resourceQuote } from '../ui/launcher';
import { QuoteView, QuoteViewOptions } from './quote-view';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';

import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';
import { isCynclyStaff } from '../../../webmodule-common/other/currentuser-claims';
import { monitorQuoteCopyCompletion } from '../../dealer-franchisee/quotes/data/quote-copy-completion-monitor';

export type TableEventQuote = (quoteSummary: ViewQuoteSummary) => Promise<void>;

export type EventLink = (linkId: string) => Promise<void>;

export interface QuoteSummaryTableOptions {
  quoteOwnerId: EventGetReference;
  numberOfDaysHistory?: number;
  quoteStates?: QuoteState[];
  customerCache: DataCacheGeneric;
  userProfileCache: DataCacheGeneric;
  title: EventSnippet;
  copyQuoteEvent: TableEventQuote;
  deleteQuoteEvent: TableEventQuote;
  /**
   * Raised after a row action has changed or removed a quote, so the HOST can refresh what lives
   * outside this table — the per-state tab counts and the page totals.
   *
   * Needed because the row-action dialog no longer routes through `QuoteListView.deleteQuote`, which
   * used to do that refresh itself; the table can only refresh its own rows. Without it a cancelled
   * quote moves to another tab while both tabs keep their old counts until something unrelated
   * happens to fire an SSE.
   */
  quoteRowActionCompletedEvent?: TableEventQuote;
  pageFragment: string;
  stale?: IStaleData;
}

const isCopyingState = (state: QuoteState): boolean => containsFlag(state, QuoteState.Copying);

//Basic class for displaying quotes in a table form
@customElement('wm-quotesummarytable')
export class QuoteSummaryTable extends DataTableWrapper<ViewQuoteSummary> {
  eventTitle: EventSnippet;
  api: QuoteApi = getApiFactory().quote();
  quoteOwnerId: EventGetReference;
  quoteStates: QuoteState[];
  customerCache: DataCacheGeneric;
  userCache: DataCacheGeneric;
  copyQuoteEvent: TableEventQuote;
  deleteQuoteEvent: TableEventQuote;
  quoteRowActionCompletedEvent?: TableEventQuote;

  private titleFilter: string | null;
  protected assignedToUserId: string | null = null;
  numberOfDaysHistory: number | undefined;
  pageFragment: string;
  dataIsStale = false;

  constructor(options: QuoteSummaryTableOptions) {
    super();
    this.eventTitle = options.title;
    this.quoteOwnerId = options.quoteOwnerId;
    this.quoteStates = options.quoteStates ?? [];
    this.customerCache = options.customerCache;
    this.userCache = options.userProfileCache;
    this.copyQuoteEvent = options.copyQuoteEvent;
    this.deleteQuoteEvent = options.deleteQuoteEvent;
    this.quoteRowActionCompletedEvent = options.quoteRowActionCompletedEvent;
    this.numberOfDaysHistory = options.numberOfDaysHistory;
    this.pageFragment = options.pageFragment;

    this.titleFilter = null;
  }

  isDataLoadDelayed() {
    return true;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewQuoteSummary>> {
    const results = await this.api.getQuoteSummary({
      numberOfDaysHistory: this.numberOfDaysHistory ?? null,
      quoteOwnerId: await this.quoteOwnerId(),
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      statesToLoad: this.quoteStates,
      title: this.titleFilter,
      description: null,
      sortField: request.sortField,
      sortAsc: request.sortAsc,
      assignedToUserId: this.assignedToUserId
    });
    if (!results) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    } else {
      //if there are any other callbacks we need to do, add them to the
      //all array which will let them run at the same time
      //pre fetch all linked data via a cache for displaying quickly reference information
      const pf = this.getPreFetched(results);
      await Promise.all(pf);

      return results.quoteSummary;
    }
  }

  async deleteQuoteItem(data: ViewQuoteSummary) {
    await this.deleteQuoteEvent?.(data);
  }

  async copyQuoteItem(data: ViewQuoteSummary) {
    await this.copyQuoteEvent?.(data);
  }

  getDefaultSortField(): string | undefined {
    return 'quoteNumber';
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` classes (which don't pierce the
    // new table's shadow DOM) to the column's own width model.
    return [
      {
        title: () => LangQuoteListView.quoteNumberTitle(),
        fieldName: 'quoteNumber',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(
            quoteSummary,
            quoteSummary.quoteNumber == 0 ? 'N/A' : quoteSummary.quoteNumber.toString()
          );
        }
      },
      {
        title: () => LangQuoteListView.titleTitle(),
        fieldName: 'title',
        minWidth: '200px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(quoteSummary, quoteSummary.quoteNumber.toString());
        }
      },
      {
        title: () => LangQuoteListView.descriptionTitle(),
        fieldName: 'description',
        minWidth: '200px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(quoteSummary, quoteSummary.description);
        }
      },
      {
        title: () => LangQuoteListView.clientTitle(),
        fieldName: 'quoteCustomerId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.getCustomerLink(quoteSummary.quoteCustomerId);
        }
      },
      {
        title: () => LangQuoteListView.createdByTitle(),
        fieldName: 'creationUserId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.getUserDisplayValue(quoteSummary.creationUserId);
        }
      },
      {
        title: () => LangQuoteListView.modifiedTitle(),
        fieldName: 'lastModifiedDate',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          const dt = new Date(quoteSummary.lastModifiedDate ?? '');
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangQuoteListView.grossTotalTitle(),
        fieldName: 'calculatedGrossTotal',
        align: 'right',
        minWidth: '100px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return moneyToHtml(quoteSummary.calculatedGrossTotal);
        }
      },
      {
        title: () => LangQuoteListView.netTotalTitle(),
        fieldName: 'calculatedNetTotal',
        align: 'right',
        minWidth: '100px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return moneyToHtml(quoteSummary.calculatedNetTotal);
        }
      }
    ];
  }

  useAutoWidthColumns(): boolean {
    return true;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(_searchTerm: string | null) {
    this.titleFilter = _searchTerm;
  }

  protected getPreFetched(results: ResultGetQuoteSummary): Promise<void>[] {
    const customerKeys = results.quoteSummary.results?.map(x => x.quoteCustomerId) ?? [];
    const userKeys = results.quoteSummary.results?.map(x => x.assignedToUserId) ?? [];

    return [this.customerCache.preFetch(customerKeys), this.userCache.preFetch(userKeys)];
  }

  protected quoteLink(row: ViewQuoteSummary, value: string) {
    if (isCopyingState(row.state)) {
      return html`<span class="quote-link quote-link-readonly" aria-disabled="true">${value}</span>`;
    }
    return html`<a class="quote-link" href="${resolveURL(resourceQuote, row.id)}" quoteid="${row.id}">${value}</a>`;
  }

  protected getUserDisplayValue(value: string) {
    if (value == emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }

  protected getCustomerLink(id: string) {
    if (id == emptyGuid) return '';
    //prefetch should already have been called
    const customer = this.customerCache.getLocal(id);
    return customer
      ? html`<a class="client-link" href="${resolveURL(resourceClient, id)}" clientid="${id}"
          >${customer.displayValue}</a
        >`
      : '';
  }

  get filterAssignedToUserId(): string | null {
    return this.assignedToUserId;
  }
}

export type QuoteViewFactory = (options: QuoteViewOptions) => QuoteView;

export interface QuoteListViewOptions {
  quoteOwnerId: EventGetReference;
  customerCache: DataCacheGeneric;
  userProfileCache: DataCacheGeneric;
}

@customElement('wm-quotelistview')
export class QuoteListView extends ViewBase {
  protected tables: QuoteSummaryTable[];
  protected readonly quoteApi: QuoteApi = getApiFactory().quote();
  protected readonly quoteOwnerId: EventGetReference;
  protected readonly elementId: string;
  protected readonly customerCache: DataCacheGeneric;
  protected readonly userProfileCache: DataCacheGeneric;
  protected tabManager: TabManager;
  //propagated to quoteeditor
  protected readonly blobApi: BlobApi = getApiFactory().blob();
  protected readonly clientApi: ClientApi = getApiFactory().client();

  constructor(options: QuoteListViewOptions) {
    super();
    //TODO - maybe we want to inject an API.. but i dont think we need to
    this.elementId = `quote-list-view-${getInternalId()}`;
    this.quoteOwnerId = options.quoteOwnerId;
    this.customerCache = options.customerCache;
    this.userProfileCache = options.userProfileCache;

    this.tables = this.initializeTables();
    this.tabManager = this.createTabManager();
  }

  public async setActiveTabByHash() {
    await this.tabManager?.applyWindowHash();
  }

  userLoggedOff() {
    //force a refresh next time we log-in
    this.tables.forEach(t => (t.lastRefresh = new Date('2000-01-01')));
  }
  protected getInitialPages(): TabPage[] {
    return this.tables.map(table => {
      const page: TabPage = {
        caption: () => table.eventTitle(),
        onEnter: () => {
          //enable data load the first time we enter the tab, but not before
          table.delayedDataLoad = false;
          // not-awaiting on purpose, for faster page switch. safe to do
          if (table.dataIsStale) table.refreshData();
        },
        content: () => {
          return table;
        },
        data: table,
        fragment: table.pageFragment
      };
      return page;
    });
  }
  createTabManager(): TabManager {
    // build static pages for each of the configured table settings
    return new TabManager({
      initialIndex: 0,
      actions: [
        {
          caption: () => html`${new IconRefresh()}`,
          onClick: async () => {
            // pages without a table payload (e.g. quote defaults) have nothing to refresh
            const table = this.tabManager.activePage?.data as QuoteSummaryTable | undefined;
            if (!table) return;
            this.beforeRefresh();
            await table.refreshData();
            this.tables.forEach(t => {
              if (t !== table) {
                t.dataIsStale = true;
              }
            });
            this.afterRefresh();
          }
        }
      ],
      addAction: {
        caption: () => LangQuoteListView.createQuote(),
        onClick: async () => {
          await this.addNewQuote();
        }
      },
      pages: () => this.getInitialPages()
    });
  }

  afterRefresh() {
    //nothing
  }

  beforeRefresh() {
    //nothing
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    //TODO-override this at another level
    return null;
  }

  async addNewQuote(): Promise<boolean> {
    const quoteContainer = await this.createNewQuote();
    if (quoteContainer?.quote) {
      return await launchQuote(quoteContainer.quoteId);
    }
    return false;
  }

  // Protected, not private: subclasses wire this into the row-action completion event so the tab
  // counts refresh for actions that no longer route through deleteQuote().
  protected async refreshTabsForState(state: number) {
    const res: Promise<void>[] = [];
    for (const t of this.tables) {
      if (flagEquals(state, ...t.quoteStates)) {
        t.refreshData();
        res.push(t.refreshData());
        this.tabManager.requestUpdate();
      }
    }
    await Promise.allSettled(res);
  }

  private async updateAndRefreshQuoteTab(switchToActiveTab?: boolean) {
    if (switchToActiveTab) {
      if (this.tabManager.activeIndex !== 0) {
        this.tables[0].refreshData();
        await this.tabManager.setActiveIndex(0);
      } else this.tables[0].refreshData();
    } else {
      this.tables[this.tabManager.activeIndex]?.refreshData();
    }
  }

  async init() {
    //override me
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): QuoteSummaryTable {
    return new QuoteSummaryTable(options);
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState[],
    pageFragment: string,
    numberOfDaysHistory: number | undefined
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDaysHistory,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      pageFragment: pageFragment
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    return [this.createSummaryTable(() => LangQuoteListView.allQuotes(), [], 'allQuotes', undefined)];
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return new QuoteContainer(quoteId, null, null, null, null, null, null, null, null); //this is a delayed load item
  }

  protected async copyQuote(quoteSummary: ViewQuoteSummary): Promise<void> {
    if (isCopyingState(quoteSummary.state)) return;
    const qcm = this.createQuoteContainerManager(this.createQuoteContainer(quoteSummary.id));
    // A quote-list copy is standalone - it is attached to no project, so there is no project address to
    // inherit and it starts blank. Only an in-project copy keeps the source's address (Task 230006).
    const copy = await qcm.makeCopy(true);
    if (copy) {
      await this.updateAndRefreshQuoteTab();
      if (!isCopyingState(copy.quote!.state)) launchQuote(copy.quoteId);
      else await monitorQuoteCopyCompletion(copy.quoteId);
    }
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return new QuoteContainerManager(quoteContainer, this.quoteApi, this.blobApi, new UiQuoteUserInteraction());
  }

  protected async deleteQuote(quoteSummary: ViewQuoteSummary): Promise<void> {
    if (isCopyingState(quoteSummary.state) && !isCynclyStaff()) return;
    const qcm = this.createQuoteContainerManager(this.createQuoteContainer(quoteSummary.id));
    const deleted = (await qcm.deleteQuote()) ?? false;

    if (deleted) {
      await this.refreshTabsForState(quoteSummary.state);
      await this.doAfterQuoteEdit();
    }
  }

  doAfterQuoteEdit() {
    //override;
  }

  protected template(): EventTemplate {
    return html` <div id=${this.elementId} class="page-content">${this.tabManager.ui}</div>`;
  }
}
